import React, { useState, useEffect } from 'react';
import { Grid, Header, Button, Dropdown, Loader } from 'semantic-ui-react';
import { PaymentTokenAction, TokenizationRequestsAction } from '../controller/actions';
import { useSelector, useDispatch } from 'react-redux';
import { useParams } from 'react-router';
import { useLocation } from 'react-router-dom';
import Decimal from 'decimal.js';

export default function PaymentCardPayCodePage(){

    const dispatch = useDispatch();
    const [disableNextBtn, setDisableNextBtn] = useState(true);
    const [surchargeAmount, setSurchargeAmount] = useState(0.0);
    const [total, setTotal] = useState(0.0);
    const [isPageError, setIsPageError] = useState(false);
    const [loader, setLoader] = useState(false);
    const [cardType, setCardType] = useState(null);
    const [cardOptions, setCardOptions] = useState([]);
    const paymentToken = useSelector((state) => state.paymentToken);
    const {
        amount,
        defaultPaymentMethod,
        takePaymentOnAuth,
        loader: tokenizationRequestLoader,
        businessUnit,
    } = useSelector((state) => state.tokenizationRequests);
    const { paymentTokenUuid } = useParams();

    useEffect(() => {
        dispatch(new PaymentTokenAction().fetchPaymentTypes());
        dispatch(new TokenizationRequestsAction().getTokenizationRequests(paymentTokenUuid));
        // eslint-disable-next-line
    }, []);

    const onCardChange = (event, data) => {
        setCardType(data.value);
        if (data.value) {
            setDisableNextBtn(false);
        }
    };

    const query = useQuery();
    function useQuery() {
        return new URLSearchParams(useLocation().search);
    }

    const onSubmit = () => {
        if (paymentTokenUuid) {
            setLoader(true);
            const successRedirectUrl = query.get('successRedirectUrl') && !query.get('successRedirectUrl') === '' ? query.get('successRedirectUrl') : null;
            const produceMessage = query.get('produceMessage') ? query.get('produceMessage').toLowerCase() : true;
            dispatch(new PaymentTokenAction().updateTokenPayment(paymentTokenUuid, cardType, successRedirectUrl, produceMessage));
        } else {
            setIsPageError(true);
        }
    };

    const isNewZealandPage = businessUnit === 'New Zealand';

    useEffect(() => {
        if (paymentToken?.cardOptions) {
            let filteredOptions = paymentToken.cardOptions.map((options) => ({...options}));

            if (businessUnit === 'New Zealand') {
                filteredOptions = filteredOptions.filter((option) => option.key !== 'amex');

                filteredOptions.forEach((option) => {
                    // Remove surcharge from text
                    option.text = option.text.replace(/\s*\(.*?\)\s*/g, '');
                    option.surcharge = 0;
                });
            }

            setCardOptions([...filteredOptions]);
        }
    }, [paymentToken, businessUnit]);

    function roundToTwoDecimals(number) {
        // alternative to .toFixed(2) that rounds down on numbers like 0.825
        // to avoid inconsistency with displaying the total amount in such cases
        return Math.round((number + Number.EPSILON) * 100) / 100;
    }


    useEffect(() => {
        if (!cardType) {
            return;
        }
        const selectedCardType = paymentToken.cardOptions.find((option) => option.value === cardType);
        const preciseAmount = new Decimal(amount);
        const surchargeMultiplier = new Decimal(selectedCardType.surcharge).dividedBy(100);
        const calculatedSurcharge = preciseAmount.times(surchargeMultiplier).toDecimalPlaces(2, Decimal.ROUND_HALF_DOWN);
        const calculatedAmount = preciseAmount.plus(calculatedSurcharge).toDecimalPlaces(2, Decimal.ROUND_HALF_DOWN);

        setSurchargeAmount(calculatedSurcharge.toNumber());
        setTotal(calculatedAmount.toNumber());
    }, [cardType]);


    return (
        <Grid verticalAlign="middle" centered className="content">
            <Grid.Row centered>
                {
                    isPageError &&
                    <Grid.Column>
                        <Header size="large">Something went wrong! Please contact help desk.</Header>
                    </Grid.Column>
                }
                {
                    !isPageError &&
                    <Grid.Column>
                        <Header size="large">CoreLogic Secure Payment</Header>
                        {(amount && !isNewZealandPage) ?
                            <>
                                <br/>
                                <p className="payment-info"><b>Payment</b> - ${roundToTwoDecimals(amount)}</p>
                                <p className="payment-info"><b>Credit Card Surcharge</b> - ${roundToTwoDecimals(surchargeAmount)}</p>
                                <p className="payment-info"><b>Total Payment</b> - ${roundToTwoDecimals(total)}</p>
                                <br/>
                            </>
                            :
                            <>
                                {
                                    amount > 0 &&
                                    <>
                                        <br/>
                                        <p><b>Payment</b> - ${amount.toFixed(2)}</p>
                                        <br/>
                                    </>
                                }
                            </>
                        }
                        <p>
                            Select your card type and then proceed to the next page to securely
                            provide your card details and validate payment.
                        </p>
                        <Header size="medium">Card Type {!isNewZealandPage && '(Fees Apply)'}</Header>
                        <Dropdown
                            fluid
                            selection
                            placeholder="-- Select Card Type --"
                            options={cardOptions}
                            onChange={onCardChange}
                            value={cardType}
                        />
                        {(tokenizationRequestLoader) && <><br/><Loader active inline size="tiny" /></>}
                        {
                            (defaultPaymentMethod || !takePaymentOnAuth) && !tokenizationRequestLoader &&
                            <div className="note" style={{ display: query.get('produceMessage') ? 'block' : 'none' }}>
                                { (defaultPaymentMethod || !takePaymentOnAuth) && 'Please note:' }
                                <ul>
                                    { (!takePaymentOnAuth) &&
                                        <li>If you proceed, a temporary pre-authorisation amount, as disclosed on the next payment page, will be added to your card for verification purposes. This amount will be released within 7 days.</li>
                                    }
                                    { (defaultPaymentMethod) &&
                                        <li>These updated payment details apply to all CoreLogic products associated with your account.</li>
                                    }
                                </ul>
                            </div>
                        }
                        <div style={{ 'marginTop': '10px' }}>
                            <Button className={disableNextBtn || (paymentToken.loader && loader) ? 'disabled-button' : ''} color="primary" onClick={onSubmit}>
                                Next {(paymentToken.loader && loader) && <Loader active inline size="tiny" />}
                            </Button>
                            <a href="javascript:history.back()"><Button style={{visibility: query.get('produceMessage') ? 'visible' : 'hidden' }} className="no-outline-primary" basic content="Cancel" /></a>

                        </div>
                    </Grid.Column>
                }
            </Grid.Row>
        </Grid>
    );
}
